import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import Modal from 'LuxKit/Modal/Modal'
import Group from 'components/utils/Group'
import TextInput from 'components/Common/Form/Input/TextInput'
import Select from 'components/Common/Form/Input/Select'
import DateOfBirthInput from 'components/Common/Form/Input/DateOfBirthInput'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { isValidPastPassengerNumber } from 'lib/cruises/pastPassengerNumber'

type PastPassengerDetails = {
  pastPassengerNumber: string
  gender: App.Cruises.ApplicableGender | ''
  title: string
  firstName: string
  lastName: string
  dateOfBirth: string
}

type PastPassengerInfoForValidation = {
  gender?: App.Cruises.ApplicableGender | ''
  title?: string
  firstName?: string
  lastName?: string
  dateOfBirth?: string
}

const PRIMARY_PASSENGER_INDEX = 0
const MASTER_TITLE_CODES = new Set(['Master', 'Mstr'])

interface Props {
  itemId: number
  passengerIndex: number
  pastPassengerNumber?: string | null
  passengerInfo?: App.PastPassengerInfo
  pastPassengerFieldsRequired: Array<string>
  personTitlesSupported: Array<App.Cruises.BookingPersonTitles>
  onSave: (details: { pastPassengerNumber: string; passengerInfo: App.PastPassengerInfo }) => void
}

export function hasPastPassengerDetails(
  pastPassengerNumber: string | null | undefined,
  passengerInfo: PastPassengerInfoForValidation | undefined,
  requiredFields: Array<string>,
): boolean {
  if (!pastPassengerNumber?.trim()) return false

  return requiredFields.every((field) => {
    if (field === 'gender') return !!passengerInfo?.gender
    if (field === 'title') return !!passengerInfo?.title?.trim()
    if (field === 'first_name') return !!passengerInfo?.firstName?.trim()
    if (field === 'last_name') return !!passengerInfo?.lastName?.trim()
    if (field === 'date_of_birth') return !!passengerInfo?.dateOfBirth?.trim()

    return true
  })
}

function PastPassengerNumberModal({
  itemId,
  passengerIndex,
  pastPassengerNumber,
  passengerInfo,
  pastPassengerFieldsRequired,
  personTitlesSupported,
  onSave,
}: Props) {
  const intl = useIntl()
  const { resolve } = useModalElementContext()
  const [details, setDetails] = useState<PastPassengerDetails>({
    pastPassengerNumber: pastPassengerNumber ?? '',
    gender: passengerInfo?.gender ?? '',
    title: passengerInfo?.title ?? '',
    firstName: passengerInfo?.firstName ?? '',
    lastName: passengerInfo?.lastName ?? '',
    dateOfBirth: passengerInfo?.dateOfBirth ?? '',
  })

  useEffect(() => {
    setDetails({
      pastPassengerNumber: pastPassengerNumber ?? '',
      gender: passengerInfo?.gender ?? '',
      title: passengerInfo?.title ?? '',
      firstName: passengerInfo?.firstName ?? '',
      lastName: passengerInfo?.lastName ?? '',
      dateOfBirth: passengerInfo?.dateOfBirth ?? '',
    })
  }, [passengerInfo, pastPassengerNumber])

  const isSaveDisabled = useMemo(() => {
    return !isValidPastPassengerNumber(details.pastPassengerNumber) ||
      !hasPastPassengerDetails(details.pastPassengerNumber, details, pastPassengerFieldsRequired)
  }, [details, pastPassengerFieldsRequired])

  const supportedGenders = useMemo(() => {
    return Array.from(new Set(personTitlesSupported.map((title) => title.applicableGender)))
  }, [personTitlesSupported])

  const supportedTitles = useMemo(() => {
    const titles = !details.gender
      ? personTitlesSupported
      : personTitlesSupported.filter((title) => title.applicableGender === details.gender)

    return passengerIndex === PRIMARY_PASSENGER_INDEX
      ? titles.filter((title) => !MASTER_TITLE_CODES.has(title.code))
      : titles
  }, [details.gender, passengerIndex, personTitlesSupported])

  const closeModal = useCallback(() => {
    resolve()
  }, [resolve])

  const handleSave = useCallback(() => {
    if (isSaveDisabled) return

    onSave({
      pastPassengerNumber: details.pastPassengerNumber.trim(),
      passengerInfo: {
        ...(details.gender && { gender: details.gender }),
        ...(details.title.trim() && { title: details.title.trim() }),
        ...(details.firstName.trim() && { firstName: details.firstName.trim() }),
        ...(details.lastName.trim() && { lastName: details.lastName.trim() }),
        ...(details.dateOfBirth.trim() && { dateOfBirth: details.dateOfBirth.trim() }),
      },
    })
    closeModal()
  }, [closeModal, details, isSaveDisabled, onSave])

  const passengerNumberLabel = intl.formatMessage({
    id: 'pfXnuuO0i-vhrZehQOUg',
    defaultMessage: 'Past passenger number',
    description: 'Modal title for cruise past passenger number information',
  })

  const invalidPassengerNumberMessage = intl.formatMessage({
    id: 'q64x2hdGLWMa-GXo8jFf',
    defaultMessage: 'Please enter a valid past passenger number',
    description: 'Validation error for an invalid cruise past passenger number entered before checkout',
  })

  const handlePastPassengerNumberValidate = useCallback((input: HTMLInputElement) => {
    input.setCustomValidity(isValidPastPassengerNumber(input.value) ? '' : invalidPassengerNumberMessage)

    return input.validity
  }, [invalidPassengerNumberMessage])

  const firstNameLabel = intl.formatMessage({
    id: 'fxmGGZdFdf7OXkAPai_9',
    defaultMessage: 'First name',
    description: 'First name input label when adding a cruise past passenger number',
  })

  const lastNameLabel = intl.formatMessage({
    id: 'g44aJ1dfy_fYZxwxLzBF',
    defaultMessage: 'Last name',
    description: 'Last name input label when adding a cruise past passenger number',
  })

  const genderLabel = intl.formatMessage({
    id: 'AgdSJWWajDHSKHPhHVHw',
    defaultMessage: 'Gender',
    description: 'Gender select label when adding a cruise past passenger number',
  })

  const titleLabel = intl.formatMessage({
    id: 'icvjyUAiMSFEUdx1FQ0l',
    defaultMessage: 'Title',
    description: 'Title select label when adding a cruise past passenger number',
  })

  const dateOfBirthLabel = intl.formatMessage({
    id: 'ybol554UNTDUq19Awy6g',
    defaultMessage: 'Date of birth',
    description: 'Date of birth input label when adding a cruise past passenger number',
  })

  const modalTitle = intl.formatMessage(
    {
      id: 'GeJ-arlOM9JlCqkuC5X8',
      defaultMessage: 'Passenger {number} past passenger number',
      description: 'Modal title for editing a cruise passenger past passenger number',
    },
    { number: passengerIndex + 1 },
  )

  return <Modal
    title={modalTitle}
    dismissible
    size="S"
    primaryActionText={intl.formatMessage({
      id: 'w3yvhWtCEeYKfkaBdhl5',
      defaultMessage: 'Save',
      description: 'Save button for cruise past passenger number details',
    })}
    secondaryActionText={intl.formatMessage({
      id: '8b-JgOAUOIIoU9qXtddH',
      defaultMessage: 'Cancel',
      description: 'Cancel button for cruise past passenger number details',
    })}
    isPrimaryActionDisabled={isSaveDisabled}
    onPrimaryActionClick={handleSave}
    onSecondaryActionClick={closeModal}
    onClose={closeModal}
  >
    <Group direction="vertical" gap={4}>
      <TextInput
        label={passengerNumberLabel}
        value={details.pastPassengerNumber}
        onChange={(event) => {
          const nextPastPassengerNumber = event.currentTarget.value
          setDetails((currentDetails) => ({ ...currentDetails, pastPassengerNumber: nextPastPassengerNumber }))
        }}
        data-testid={`occupancy-item-${itemId}-past-passenger-number-input`}
        optionalIndicatorEnabled={false}
        invalidErrorMessage={invalidPassengerNumberMessage}
        onInputValidate={handlePastPassengerNumberValidate}
      />
      {pastPassengerFieldsRequired.includes('gender') && <Select
        label={genderLabel}
        value={details.gender}
        onChange={(event) => {
          const gender = event.currentTarget.value as App.Cruises.ApplicableGender
          setDetails((currentDetails) => ({
            ...currentDetails,
            gender,
            title: personTitlesSupported.some((title) => (
              title.code === currentDetails.title && title.applicableGender === gender
            )) ? currentDetails.title : '',
          }))
        }}
        data-testid={`occupancy-item-${itemId}-past-passenger-gender-select`}
        optionalIndicatorEnabled={false}
        required
      >
        {supportedGenders.map((gender) => <option key={gender} value={gender}>{gender}</option>)}
      </Select>}
      {pastPassengerFieldsRequired.includes('title') && <Select
        label={titleLabel}
        value={details.title}
        onChange={(event) => {
          const title = event.currentTarget.value
          setDetails((currentDetails) => ({ ...currentDetails, title }))
        }}
        data-testid={`occupancy-item-${itemId}-past-passenger-title-select`}
        optionalIndicatorEnabled={false}
        required
      >
        {supportedTitles.map((title) => (
          <option key={title.id} value={title.code}>{title.name}</option>
        ))}
      </Select>}
      {pastPassengerFieldsRequired.includes('first_name') && <TextInput
        label={firstNameLabel}
        value={details.firstName}
        onChange={(event) => {
          const firstName = event.currentTarget.value
          setDetails((currentDetails) => ({ ...currentDetails, firstName }))
        }}
        data-testid={`occupancy-item-${itemId}-past-passenger-first-name-input`}
        optionalIndicatorEnabled={false}
      />}
      {pastPassengerFieldsRequired.includes('last_name') && <TextInput
        label={lastNameLabel}
        value={details.lastName}
        onChange={(event) => {
          const lastName = event.currentTarget.value
          setDetails((currentDetails) => ({ ...currentDetails, lastName }))
        }}
        data-testid={`occupancy-item-${itemId}-past-passenger-last-name-input`}
        optionalIndicatorEnabled={false}
      />}
      {pastPassengerFieldsRequired.includes('date_of_birth') && <DateOfBirthInput
        label={dateOfBirthLabel}
        value={details.dateOfBirth}
        onChange={(_date, dateOfBirth) => {
          setDetails((currentDetails) => ({ ...currentDetails, dateOfBirth: dateOfBirth ?? '' }))
        }}
        data-testid={`occupancy-item-${itemId}-past-passenger-date-of-birth-input`}
        optionalIndicatorEnabled={false}
        required
      />}
    </Group>
  </Modal>
}

export default PastPassengerNumberModal
